import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../../lib/api.js";
import { PageSpinner } from "../../components/ui.js";

interface Pref { id: string; item: string; rating: string }

const RATINGS: [string, string, string][] = [
  ["love", "Love", "badge-success"],
  ["like", "Like", "badge-accent"],
  ["neutral", "Neutral", "badge-muted"],
  ["dislike", "Dislike", "badge-warning"],
  ["never", "Never", "badge-danger"]
];

export function PreferencesTab() {
  const [prefs, setPrefs] = useState<Pref[] | null>(null);
  const [item, setItem] = useState("");
  const [rating, setRating] = useState("like");
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setPrefs((await api.get<{ preferences: Pref[] }>("/api/nutrition/preferences")).preferences);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load preferences.");
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function add() {
    if (!item.trim()) return;
    await api.put("/api/nutrition/preferences", { item: item.trim(), rating }).catch(() => undefined);
    setItem("");
    await load();
  }
  async function remove(id: string) {
    await api.del(`/api/nutrition/preferences/${id}`).catch(() => undefined);
    await load();
  }

  if (!prefs) return error ? <div className="alert alert-danger">{error}</div> : <PageSpinner />;

  return (
    <div className="stack">
      <div className="card">
        <div className="card-header"><span className="card-title">Add a preference</span></div>
        <p className="dim text-xs" style={{ marginTop: -4 }}>Foods, cuisines, proteins — anything. ClovaForm uses these to pick meals you'll actually eat and avoid the ones you won't.</p>
        <div className="field mt-4">
          <input className="input" value={item} onChange={(e) => setItem(e.target.value)} placeholder="e.g. Salmon, Mushrooms, Thai food" onKeyDown={(e) => { if (e.key === "Enter") void add(); }} />
        </div>
        <div className="choice-grid">
          {RATINGS.map(([v, l]) => (
            <button key={v} type="button" className={`choice${rating === v ? " selected" : ""}`} onClick={() => setRating(v)}>{l}</button>
          ))}
        </div>
        <button className="btn btn-primary mt-4" onClick={add} disabled={!item.trim()}>Save preference</button>
      </div>

      {prefs.length > 0 && (
        <div className="card">
          <div className="card-header"><span className="card-title">Your preferences</span></div>
          <div className="stack">
            {prefs.map((p) => {
              const r = RATINGS.find((x) => x[0] === p.rating);
              return (
                <div className="row-between" key={p.id}>
                  <span>{p.item}</span>
                  <div className="row">
                    <span className={`badge ${r?.[2] ?? "badge-muted"}`}>{r?.[1] ?? p.rating}</span>
                    <button className="btn btn-sm btn-ghost" onClick={() => remove(p.id)}>✕</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
